/**
 * FibonacciPi SEO, Analytics & Search Engine Integration Engine
 *
 * Provides complete client-side support for:
 * 1. Google Analytics 4 (GA4) auto-loading & route change tracking
 * 2. Google Tag Manager (GTM) container injection
 * 3. Google Search Console & Bing Webmaster verification meta tags
 * 4. Dynamic Canonical URLs per route
 * 5. Open Graph (OG) & Twitter Card social meta tags
 * 6. Schema.org / JSON-LD structured data (WebSite, Organization, JobPosting, Breadcrumbs)
 * 7. Real-User Core Web Vitals (CWV) performance monitoring
 */

export interface SeoConfig {
  gaMeasurementId?: string
  gtmContainerId?: string
  googleSiteVerification?: string
  bingSiteVerification?: string
  canonicalBaseUrl?: string
  metaTitleDefault?: string
  metaDescriptionDefault?: string
  ogImageDefault?: string
  twitterSite?: string
  enableSchemaOrg?: boolean
  enableWebVitals?: boolean
}

export interface PageSeoProps {
  title?: string
  description?: string
  canonicalPath?: string
  ogType?: 'website' | 'article' | 'job' | 'profile' | string
  ogImage?: string | null
  jsonLd?: Record<string, any> | Array<Record<string, any>>
  noindex?: boolean
}

declare global {
  interface Window {
    dataLayer: any[]
    gtag: (...args: any[]) => void
    _fbpi_ga_initialized?: boolean
    _fbpi_gtm_initialized?: boolean
    _fbpi_vitals_initialized?: boolean
  }
}

let activeConfig: SeoConfig = {
  canonicalBaseUrl: 'https://fibonaccipi.com',
  metaTitleDefault: 'FibonacciPi · Verified Jobs, Career Hub & Smart Hiring',
  metaDescriptionDefault:
    'Verified Jobs. Smart Matching. Better Hiring. Explore 100% verified opportunities with transparent take-home salary breakdowns, fresher launchpad, and safe hiring guarantee.',
  ogImageDefault: 'https://fibonaccipi.com/logo-full.png',
  twitterSite: '@fibonaccipi',
  enableSchemaOrg: true,
  enableWebVitals: true,
}

/**
 * Configure global SEO and analytics credentials fetched from /api/v1/config
 */
export function initSeoConfig(settings: Record<string, any>) {
  activeConfig = {
    gaMeasurementId: settings['seo.ga_measurement_id'] || '',
    gtmContainerId: settings['seo.gtm_container_id'] || '',
    googleSiteVerification: settings['seo.google_site_verification'] || '',
    bingSiteVerification: settings['seo.bing_site_verification'] || '',
    canonicalBaseUrl: settings['seo.canonical_base_url'] || 'https://fibonaccipi.com',
    metaTitleDefault: settings['seo.meta_title_default'] || activeConfig.metaTitleDefault,
    metaDescriptionDefault: settings['seo.meta_description_default'] || activeConfig.metaDescriptionDefault,
    ogImageDefault: settings['seo.og_image_default'] || activeConfig.ogImageDefault,
    twitterSite: settings['seo.twitter_site'] || activeConfig.twitterSite,
    enableSchemaOrg: settings['seo.enable_schema_org'] !== false,
    enableWebVitals: settings['seo.enable_web_vitals'] !== false,
  }

  // 1. Initialize Google Search Console & Bing Verification tags
  applyVerificationTags(activeConfig)

  // 2. Initialize Google Analytics 4
  if (activeConfig.gaMeasurementId && !window._fbpi_ga_initialized) {
    loadGoogleAnalytics(activeConfig.gaMeasurementId)
  }

  // 3. Initialize Google Tag Manager
  if (activeConfig.gtmContainerId && !window._fbpi_gtm_initialized) {
    loadGoogleTagManager(activeConfig.gtmContainerId)
  }

  // 4. Initialize Core Web Vitals Monitoring
  if (activeConfig.enableWebVitals && !window._fbpi_vitals_initialized) {
    initWebVitalsMonitoring()
  }

  // 5. Inject Base Schema.org Structured Data
  if (activeConfig.enableSchemaOrg) {
    injectGlobalSchema(activeConfig)
  }
}

/**
 * Update HTML meta verification tags
 */
function applyVerificationTags(config: SeoConfig) {
  if (typeof document === 'undefined') return

  if (config.googleSiteVerification) {
    let meta = document.querySelector('meta[name="google-site-verification"]') as HTMLMetaElement
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'google-site-verification'
      document.head.appendChild(meta)
    }
    meta.content = config.googleSiteVerification
  }

  if (config.bingSiteVerification) {
    let meta = document.querySelector('meta[name="msvalidate.01"]') as HTMLMetaElement
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'msvalidate.01'
      document.head.appendChild(meta)
    }
    meta.content = config.bingSiteVerification
  }
}

/**
 * Load Google Analytics 4 script asynchronously
 */
function loadGoogleAnalytics(measurementId: string) {
  if (typeof document === 'undefined') return
  window._fbpi_ga_initialized = true

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  document.head.appendChild(script)

  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    window.dataLayer.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', measurementId, {
    page_path: window.location.pathname + window.location.search,
    send_page_view: true,
  })
}

/**
 * Load Google Tag Manager container script
 */
function loadGoogleTagManager(gtmId: string) {
  if (typeof document === 'undefined') return
  window._fbpi_gtm_initialized = true

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
  document.head.appendChild(script)
}

/**
 * Track page view on route change
 */
export function trackPageView(urlPath: string, pageTitle?: string) {
  if (typeof window === 'undefined') return

  if (window.gtag && activeConfig.gaMeasurementId) {
    window.gtag('config', activeConfig.gaMeasurementId, {
      page_path: urlPath,
      page_title: pageTitle || document.title,
    })
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'pageview',
      page_path: urlPath,
      page_title: pageTitle || document.title,
    })
  }
}

/**
 * Real-User Core Web Vitals Monitoring (LCP, INP, CLS, FCP, TTFB)
 */
function initWebVitalsMonitoring() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return
  window._fbpi_vitals_initialized = true

  const reportMetric = (name: string, value: number, rating: string) => {
    if (window.gtag) {
      window.gtag('event', 'web_vitals', {
        event_category: 'Web Vitals',
        event_label: name,
        value: Math.round(name === 'CLS' ? value * 1000 : value),
        metric_rating: rating,
        non_interaction: true,
      })
    }
  }

  try {
    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries()
      const lastEntry = entries[entries.length - 1] as any
      if (lastEntry) {
        const value = lastEntry.renderTime || lastEntry.loadTime
        const rating = value <= 2500 ? 'good' : value <= 4000 ? 'needs_improvement' : 'poor'
        reportMetric('LCP', value, rating)
      }
    })
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true })

    // Cumulative Layout Shift (CLS)
    let clsScore = 0
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsScore += entry.value
        }
      }
      const rating = clsScore <= 0.1 ? 'good' : clsScore <= 0.25 ? 'needs_improvement' : 'poor'
      reportMetric('CLS', clsScore, rating)
    })
    clsObserver.observe({ type: 'layout-shift', buffered: true })

    // First Contentful Paint (FCP)
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entry = entryList.getEntriesByName('first-contentful-paint')[0]
      if (entry) {
        const rating = entry.startTime <= 1800 ? 'good' : entry.startTime <= 3000 ? 'needs_improvement' : 'poor'
        reportMetric('FCP', entry.startTime, rating)
      }
    })
    fcpObserver.observe({ type: 'paint', buffered: true })
  } catch (e) {
    // PerformanceObserver feature unsupported gracefully handled
  }
}

/**
 * Inject base WebSite and Organization Schema.org structured data
 */
function injectGlobalSchema(config: SeoConfig) {
  if (typeof document === 'undefined') return

  const baseUrl = config.canonicalBaseUrl || 'https://fibonaccipi.com'

  const globalSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        'url': baseUrl,
        'name': 'FibonacciPi',
        'description': config.metaDescriptionDefault,
        'potentialAction': {
          '@type': 'SearchAction',
          'target': `${baseUrl}/jobs?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        'name': 'FibonacciPi',
        'url': baseUrl,
        'logo': config.ogImageDefault || `${baseUrl}/logo-full.png`,
        'sameAs': [
          'https://twitter.com/fibonaccipi',
          'https://www.linkedin.com/company/fibonaccipi',
        ],
      },
    ],
  }

  let script = document.getElementById('global-jsonld-schema') as HTMLScriptElement
  if (!script) {
    script = document.createElement('script')
    script.id = 'global-jsonld-schema'
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(globalSchema)
}

/**
 * Apply dynamic SEO meta tags, canonical link, and JSON-LD schema per page
 */
export function applyPageSeo(props: PageSeoProps) {
  if (typeof document === 'undefined') return

  const title = props.title ? `${props.title} · FibonacciPi` : activeConfig.metaTitleDefault || 'FibonacciPi'
  const description = props.description || activeConfig.metaDescriptionDefault || ''
  const canonicalUrl = `${activeConfig.canonicalBaseUrl || 'https://fibonaccipi.com'}${props.canonicalPath || window.location.pathname}`
  const ogImage = props.ogImage || activeConfig.ogImageDefault || 'https://fibonaccipi.com/logo-full.png'
  const ogType = props.ogType || 'website'

  // Document title
  document.title = title

  // Meta description
  setMetaTag('name', 'description', description)

  // Robots indexing
  setMetaTag('name', 'robots', props.noindex ? 'noindex, nofollow' : 'index, follow')

  // Canonical Link
  let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    document.head.appendChild(canonicalLink)
  }
  canonicalLink.href = canonicalUrl

  // Open Graph
  setMetaTag('property', 'og:title', title)
  setMetaTag('property', 'og:description', description)
  setMetaTag('property', 'og:url', canonicalUrl)
  setMetaTag('property', 'og:type', ogType)
  setMetaTag('property', 'og:image', ogImage)
  setMetaTag('property', 'og:site_name', 'FibonacciPi')

  // Twitter Card
  setMetaTag('name', 'twitter:card', 'summary_large_image')
  setMetaTag('name', 'twitter:title', title)
  setMetaTag('name', 'twitter:description', description)
  setMetaTag('name', 'twitter:image', ogImage)
  if (activeConfig.twitterSite) {
    setMetaTag('name', 'twitter:site', activeConfig.twitterSite)
  }

  // Page Specific JSON-LD Schema
  if (activeConfig.enableSchemaOrg && props.jsonLd) {
    let script = document.getElementById('page-jsonld-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'page-jsonld-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(props.jsonLd)
  } else {
    const existing = document.getElementById('page-jsonld-schema')
    if (existing) existing.remove()
  }

  // Track page view
  trackPageView(window.location.pathname + window.location.search, title)
}

function setMetaTag(attr: 'name' | 'property', attrValue: string, content: string) {
  let meta = document.querySelector(`meta[${attr}="${attrValue}"]`) as HTMLMetaElement
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, attrValue)
    document.head.appendChild(meta)
  }
  meta.content = content
}
